import { Node, Edge } from 'reactflow';

interface IFlowToJsonOptions {
    assessmentUuid: string;
    questions: any[];
    nodeConfigs: Record<string, any>;
}

interface IRule {
    id: string;
    question: {
        id: string;
        title: string;
        type: string;
    };
    answer: {
        value: string | string[] | number;
        id: string | string[] | null;
        comparison?: {
            operator: string;
        };
    };
    result: {
        type: 'not_suitable' | 'text_visit' | 'continue' | 'add_note';
        skipToQuestionId?: string;
        selectedOptions?: string[];
        customText?: string;
    };
    triggerTiming: 'immediate' | 'after_last_question';
}

interface IJsonOutput {
    assessmentUuid: string;
    rules: IRule[];
}

/**
 * 将流程图转换为SimpleRuleBuilder兼容的JSON格式
 */
export const flowToJson = (
    nodes: Node[],
    edges: Edge[],
    options: IFlowToJsonOptions
): IJsonOutput => {
    const { assessmentUuid, questions, nodeConfigs } = options;
    
    // 过滤出问题节点
    const questionNodes = nodes.filter(node => node.type === 'question');
    
    const rules: IRule[] = questionNodes.map(questionNode => {
        const config = nodeConfigs[questionNode.id];
        if (!config) return null;

        // 找到从该问题节点出发的边
        const outgoingEdges = edges.filter(edge => edge.source === questionNode.id);
        
        // 找到对应的结果节点
        const resultEdge = outgoingEdges.find(edge => {
            const targetNode = nodes.find(n => n.id === edge.target);
            return targetNode?.type === 'result';
        });
        
        const resultNode = resultEdge ? nodes.find(n => n.id === resultEdge.target) : null;
        const resultConfig = resultNode ? nodeConfigs[resultNode.id] : null;

        if (!resultConfig) return null;

        // 获取问题信息
        const question = questions.find(q => q.id === config.questionId);
        if (!question) return null;

        // 构建规则对象
        const rule: IRule = {
            id: questionNode.id,
            question: {
                id: config.questionId,
                title: question.title,
                type: question.type || 'questionFrom',
            },
            answer: {
                value: config.answerCondition || '',
                id: null,
            },
            result: {
                type: resultConfig.resultType,
            },
            triggerTiming: config.triggerTiming || 'after_last_question',
        };

        // 添加特殊结果类型的额外字段
        if (resultConfig.resultType === 'continue' && resultConfig.skipToQuestionId) {
            rule.result.skipToQuestionId = resultConfig.skipToQuestionId;
        }

        if (resultConfig.resultType === 'add_note') {
            if (resultConfig.selectedOptions && resultConfig.selectedOptions.length > 0) {
                rule.result.selectedOptions = resultConfig.selectedOptions;
            }
            if (resultConfig.customText) {
                rule.result.customText = resultConfig.customText;
            }
        }

        // 添加比较模式（如果是ChoiceQuestion）
        if (question.type === 'ChoiceQuestion' || question.type === 'choice_question') {
            rule.answer.comparison = {
                operator: 'equals', // 默认等于，可以根据需要调整
            };
        }

        return rule;
    }).filter(rule => rule !== null) as IRule[];

    return {
        assessmentUuid,
        rules,
    };
};

/**
 * 验证流程图是否有效
 */
export const validateFlow = (
    nodes: Node[],
    edges: Edge[],
    nodeConfigs: Record<string, any>
): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];

    // 检查是否有开始节点
    const startNodes = nodes.filter(node => node.type === 'start');
    if (startNodes.length === 0) {
        errors.push('Flow must have at least one start node');
    }

    // 检查问题节点配置
    const questionNodes = nodes.filter(node => node.type === 'question');
    questionNodes.forEach(node => {
        const config = nodeConfigs[node.id];
        if (!config || !config.questionId) {
            errors.push(`Question node ${node.id} is not properly configured`);
        }
    });

    // 检查结果节点配置
    const resultNodes = nodes.filter(node => node.type === 'result');
    resultNodes.forEach(node => {
        const config = nodeConfigs[node.id];
        if (!config || !config.resultType) {
            errors.push(`Result node ${node.id} is not properly configured`);
        }
    });

    // 检查连线
    edges.forEach(edge => {
        const sourceNode = nodes.find(n => n.id === edge.source);
        const targetNode = nodes.find(n => n.id === edge.target);
        
        if (!sourceNode || !targetNode) {
            errors.push(`Edge ${edge.id} has invalid source or target node`);
        }
    });

    return {
        isValid: errors.length === 0,
        errors,
    };
};
